import {
  Brain,
  Database,
  Download,
  Filter,
  File,
  FileText,
  Info,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  BuyTokensModal,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmModal,
  Input,
  InsufficientTokensModal,
  LoadingSpinner,
  Modal,
  Pagination,
  ViewToggle,
} from '../components/ui';
import { useAuth, useOrganization, usePage } from '../contexts';
import { useLocalStorage, useTokens } from '../hooks';
import { knowledgeSourceService } from '../services';
import { KnowledgeSource, RAGRequest, SearchRequest } from '../types';
import { normalizeTags } from '../utils';

const KnowledgeBase: React.FC = () => {
  const MAX_FILES_PER_UPLOAD = 15;
  const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1024MB
  const ALLOWED_TYPES = [
    'application/pdf',
    'text/csv',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  const ALLOWED_EXTENSIONS = ['.pdf', '.csv', '.txt', '.docx', '.xls', '.xlsx'];

  const { t } = useTranslation();
  const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSource[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [listFilterQuery, setListFilterQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'ready' | 'processing' | 'error' | 'pending'
  >('all');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showRAGModal, setShowRAGModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChunksModal, setShowChunksModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);
  const [selectedKnowledgeSource, setSelectedKnowledgeSource] =
    useState<KnowledgeSource | null>(null);
  const [chunks, setChunks] = useState<any[]>([]);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    tags: '',
  });
  const [ragQuery, setRagQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [ragResults, setRagResults] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isRAGing, setIsRAGing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalSources, setTotalSources] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useLocalStorage<'full' | 'compact'>(
    'knowledgeBase_viewMode',
    'full'
  );

  const { currentOrganization } = useOrganization();
  const { setPageInfo } = usePage();
  const {
    checkTokensBeforeAction,
    tokensBalance,
    isModalOpen,
    closeModal,
    isBuyTokensModalOpen,
    closeBuyTokensModal,
  } = useTokens();
  const { refreshUser } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);

  const availableFileTypes = useMemo(() => {
    const fileTypes = new Set(
      knowledgeSources.map(source => source.file_type).filter(Boolean)
    );
    return Array.from(fileTypes).sort();
  }, [knowledgeSources]);

  const availableTags = useMemo(() => {
    const tags = new Set(
      knowledgeSources.flatMap(source => normalizeTags(source.tags || []))
    );
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [knowledgeSources]);

  const filteredKnowledgeSources = useMemo(() => {
    return knowledgeSources.filter(source => {
      const normalizedQuery = listFilterQuery.trim().toLowerCase();
      if (normalizedQuery) {
        const matchesQuery =
          source.name.toLowerCase().includes(normalizedQuery) ||
          (source.description || '').toLowerCase().includes(normalizedQuery) ||
          normalizeTags(source.tags || [])
            .join(' ')
            .toLowerCase()
            .includes(normalizedQuery);
        if (!matchesQuery) {
          return false;
        }
      }

      if (statusFilter !== 'all') {
        const isReady =
          source.processing_status === 'completed' &&
          source.embeddings_status === 'completed';
        const isProcessing =
          source.processing_status === 'processing' ||
          source.embeddings_status === 'processing';
        const isError =
          source.processing_status === 'error' ||
          source.embeddings_status === 'error' ||
          Boolean(source.processing_error) ||
          Boolean(source.embeddings_error);
        const isPending =
          source.processing_status === 'pending' ||
          source.embeddings_status === 'pending';

        if (
          (statusFilter === 'ready' && !isReady) ||
          (statusFilter === 'processing' && !isProcessing) ||
          (statusFilter === 'error' && !isError) ||
          (statusFilter === 'pending' && !isPending)
        ) {
          return false;
        }
      }

      if (fileTypeFilter !== 'all' && source.file_type !== fileTypeFilter) {
        return false;
      }

      if (tagFilter !== 'all') {
        const sourceTags = normalizeTags(source.tags || []);
        if (!sourceTags.includes(tagFilter)) {
          return false;
        }
      }

      return true;
    });
  }, [fileTypeFilter, knowledgeSources, listFilterQuery, statusFilter, tagFilter]);

  const loadKnowledgeSources = useCallback(async () => {
    if (!currentOrganization) return;

    try {
      setIsLoading(true);
      setActionError(null);
      const response = await knowledgeSourceService.getAll({
        organization_id: currentOrganization.id,
        page: currentPage,
        page_size: pageSize,
      });
      setKnowledgeSources(response.items || []);
      setTotalSources(response.total || 0);
      setTotalPages(Math.max(1, response.total_pages || 1));
    } catch (error) {
      setActionError(t('knowledgeBase.errorLoadingKnowledgeBase'));
      toast.error(t('knowledgeBase.errorLoadingKnowledgeBase'));
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization, t, currentPage, pageSize]);

  const handleRefresh = useCallback(async () => {
    await loadKnowledgeSources();
    // Atualizar tokens após atualizar base de conhecimento
    await refreshUser();
  }, [loadKnowledgeSources, refreshUser]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentOrganization]);

  useEffect(() => {
    if (!currentOrganization) {
      return;
    }

    loadKnowledgeSources();
  }, [currentOrganization, loadKnowledgeSources]);

  useEffect(() => {
    if (!currentOrganization) {
      return;
    }

    const hasOngoingPipeline = knowledgeSources.some(
      source =>
        source.processing_status === 'processing' ||
        source.embeddings_status === 'processing'
    );

    if (!hasOngoingPipeline) {
      return;
    }

    const intervalId = window.setInterval(() => {
      loadKnowledgeSources();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentOrganization, knowledgeSources, loadKnowledgeSources]);

  // Set page title and description
  useEffect(() => {
    setPageInfo(t('knowledgeBase.title'), t('knowledgeBase.subtitle'));
  }, [setPageInfo, t]);

  const validateAndSetFiles = (incomingFiles: File[]) => {
    const uniqueByNameAndSize = Array.from(
      new Map(incomingFiles.map(file => [`${file.name}:${file.size}`, file])).values()
    );
    const limitedFiles = uniqueByNameAndSize.slice(0, MAX_FILES_PER_UPLOAD);

    if (uniqueByNameAndSize.length > MAX_FILES_PER_UPLOAD) {
      toast.error(`Limite de ${MAX_FILES_PER_UPLOAD} arquivos por upload.`);
    }

    const validFiles: File[] = [];
    const rejectedFiles: string[] = [];
    limitedFiles.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        rejectedFiles.push(`${file.name} (${t('knowledgeBase.fileTooLarge')})`);
        return;
      }

      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (
        !ALLOWED_TYPES.includes(file.type) &&
        !ALLOWED_EXTENSIONS.includes(fileExtension)
      ) {
        rejectedFiles.push(`${file.name} (${t('knowledgeBase.unsupportedFileType')})`);
        return;
      }

      validFiles.push(file);
    });

    if (rejectedFiles.length > 0) {
      toast.error(rejectedFiles[0]);
    }

    if (validFiles.length === 0) {
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(validFiles);
    if (validFiles.length === 1) {
      const file = validFiles[0];
      setUploadData(prev => ({
        ...prev,
        name: file.name.split('.')[0],
        description:
          prev.description ||
          `${t('knowledgeBase.file')} ${file.name} ${t(
            'knowledgeBase.addedToKnowledgeBase'
          )}`,
      }));
      return;
    }

    setUploadData(prev => ({
      ...prev,
      name: '',
      description:
        prev.description ||
        `${validFiles.length} arquivos ${t('knowledgeBase.addedToKnowledgeBase')}`,
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    validateAndSetFiles(files);
    e.target.value = '';
  };

  const handleDropFiles = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    validateAndSetFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !currentOrganization) {
      toast.error(t('knowledgeBase.fillRequiredFields'));
      return;
    }

    // Verificar tokens antes de fazer upload
    if (!checkTokensBeforeAction()) {
      return;
    }

    try {
      setIsUploading(true);
      setActionError(null);
      const tags = uploadData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);

      // Determinar tipo do arquivo baseado na extensão
      const getFileType = (file: File): string => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension === 'pdf') return 'pdf';
        if (extension === 'csv') return 'csv';
        if (extension === 'txt') return 'txt';
        if (extension === 'docx') return 'docx';
        if (extension === 'xls') return 'xls';
        if (extension === 'xlsx') return 'xlsx';
        return 'txt'; // fallback
      };

      const uploadRequests = selectedFiles.map(file =>
        knowledgeSourceService.upload(
          {
            name:
              selectedFiles.length === 1
                ? uploadData.name || file.name.split('.')[0]
                : file.name.split('.')[0],
            description:
              uploadData.description ||
              `${t('knowledgeBase.file')} ${file.name} ${t(
                'knowledgeBase.addedToKnowledgeBase'
              )}`,
            file_type: getFileType(file),
            tags,
            organization_id: currentOrganization.id,
          },
          file
        )
      );
      const results = await Promise.allSettled(uploadRequests);
      const successItems = results
        .filter(
          (result): result is PromiseFulfilledResult<KnowledgeSource> =>
            result.status === 'fulfilled'
        )
        .map(result => result.value);
      const failedCount = results.length - successItems.length;

      if (successItems.length > 0) {
        setKnowledgeSources(prev => [...successItems, ...prev]);
      }
      setShowUploadModal(false);
      setSelectedFiles([]);
      setUploadData({ name: '', description: '', tags: '' });
      if (failedCount > 0 && successItems.length > 0) {
        toast.success(`${successItems.length} arquivos enviados. ${failedCount} falharam.`);
      } else if (failedCount > 0) {
        toast.error('Nao foi possivel enviar os arquivos selecionados.');
      } else {
        toast.success(t('knowledgeBase.fileUploadedSuccessfully'));
      }

      // Atualizar tokens após interação com IA
      await refreshUser();
    } catch (error) {
      setActionError(t('knowledgeBase.errorUploadingFile'));
      toast.error(t('knowledgeBase.errorUploadingFile'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !currentOrganization) return;

    // Verificar tokens antes de fazer busca RAG
    if (!checkTokensBeforeAction()) {
      return;
    }

    try {
      setIsSearching(true);
      setActionError(null);
      const searchRequest: SearchRequest = {
        query: searchQuery,
        organization_id: currentOrganization.id,
        limit: 10,
        similarity_threshold: 0.7,
      };

      const results = await knowledgeSourceService.search(searchRequest);
      setSearchResults(results.results);
      setShowSearchModal(true);

      // Atualizar tokens após interação com IA
      await refreshUser();
    } catch (error) {
      setActionError(t('knowledgeBase.errorSearchingKnowledgeBase'));
      toast.error(t('knowledgeBase.errorSearchingKnowledgeBase'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleRAGQuery = async () => {
    if (!ragQuery.trim() || !currentOrganization) return;

    // Verificar tokens antes de executar consulta RAG
    if (!checkTokensBeforeAction()) {
      return;
    }

    try {
      setIsRAGing(true);
      setActionError(null);
      const ragRequest: RAGRequest = {
        query: ragQuery,
        organization_id: currentOrganization.id,
        max_context_length: 4000,
        similarity_threshold: 0.7,
        limit: 5,
      };

      const results = await knowledgeSourceService.ragQuery(ragRequest);
      setRagResults(results);
      setShowRAGModal(true);

      // Atualizar tokens após interação com IA
      await refreshUser();
    } catch (error) {
      setActionError(t('knowledgeBase.errorExecutingRAGQuery'));
      toast.error(t('knowledgeBase.errorExecutingRAGQuery'));
    } finally {
      setIsRAGing(false);
    }
  };

  const handleProcess = async (id: number) => {
    try {
      setActionError(null);
      await knowledgeSourceService.process(id);
      toast.success(t('knowledgeBase.processingStarted'));
      loadKnowledgeSources();
    } catch (error) {
      setActionError(t('knowledgeBase.errorStartingProcessing'));
      toast.error(t('knowledgeBase.errorStartingProcessing'));
    }
  };

  const handleGenerateEmbeddings = async (id: number) => {
    try {
      setActionError(null);
      await knowledgeSourceService.generateEmbeddings(id);
      toast.success(t('knowledgeBase.embeddingsGenerationStarted'));
      loadKnowledgeSources();
    } catch (error) {
      setActionError(t('knowledgeBase.errorGeneratingEmbeddings'));
      toast.error(t('knowledgeBase.errorGeneratingEmbeddings'));
    }
  };

  const handleDeleteClick = (id: number) => {
    setFileToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;

    try {
      setActionError(null);
      await knowledgeSourceService.delete(fileToDelete);
      setKnowledgeSources(prev => prev.filter(ks => ks.id !== fileToDelete));
      toast.success(t('knowledgeBase.fileRemovedSuccessfully'));
      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (error) {
      setActionError(t('knowledgeBase.errorRemovingFile'));
      toast.error(t('knowledgeBase.errorRemovingFile'));
    }
  };

  const handleViewChunks = async (source: KnowledgeSource) => {
    if (!currentOrganization) return;

    // Verificar tokens antes de carregar chunks
    if (!checkTokensBeforeAction()) {
      return;
    }

    try {
      setIsLoadingChunks(true);
      setActionError(null);
      setSelectedKnowledgeSource(source);
      const response = await knowledgeSourceService.getChunksByKnowledgeSource(
        source.id,
        currentOrganization.id,
        100
      );
      setChunks(response.results || []);
      setShowChunksModal(true);

      // Atualizar tokens após interação
      await refreshUser();
    } catch (error) {
      setActionError(t('knowledgeBase.errorLoadingChunks'));
      toast.error(t('knowledgeBase.errorLoadingChunks'));
    } finally {
      setIsLoadingChunks(false);
    }
  };

  const getStatusBadge = (source: KnowledgeSource) => {
    if (
      source.processing_status === 'completed' &&
      source.embeddings_status === 'completed'
    ) {
      return <Badge variant="success">{t('knowledgeBase.ready')}</Badge>;
    } else if (source.processing_status === 'processing') {
      return <Badge variant="warning">{t('knowledgeBase.processing')}</Badge>;
    } else if (source.processing_status === 'error') {
      return <Badge variant="error">{t('knowledgeBase.error')}</Badge>;
    } else {
      return <Badge variant="secondary">{t('knowledgeBase.pending')}</Badge>;
    }
  };

  const renderPipeline = (source: KnowledgeSource) => {
    const uploadedDone = true;
    const processedDone = source.processing_status === 'completed';
    const embeddedDone = source.embeddings_status === 'completed';
    const processingError =
      source.processing_status === 'error' || Boolean(source.processing_error);
    const embeddingError =
      source.embeddings_status === 'error' || Boolean(source.embeddings_error);

    return (
      <div className="flex items-center gap-1 flex-wrap">
        <Badge variant={uploadedDone ? 'success' : 'secondary'} size="sm">
          Uploaded
        </Badge>
        <Badge
          variant={
            processingError
              ? 'error'
              : processedDone
                ? 'success'
                : source.processing_status === 'processing'
                  ? 'warning'
                  : 'secondary'
          }
          size="sm"
        >
          Processed
        </Badge>
        <Badge
          variant={
            embeddingError
              ? 'error'
              : embeddedDone
                ? 'success'
                : source.embeddings_status === 'processing'
                  ? 'warning'
                  : 'secondary'
          }
          size="sm"
        >
          Embedded
        </Badge>
      </div>
    );
  };

  const getFileIcon = (fileType: string) => {
    if (fileType === 'pdf') {
      return <FileText className="h-5 w-5 text-red-600" />;
    } else if (fileType === 'csv') {
      return <Database className="h-5 w-5 text-green-600" />;
    } else if (fileType === 'txt') {
      return <FileText className="h-5 w-5 text-blue-600" />;
    } else if (fileType === 'docx') {
      return <FileText className="h-5 w-5 text-blue-700 dark:text-blue-400" />;
    }
    return <File className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-3">
      {actionError && (
        <div className="flex items-center justify-between gap-3 p-3 border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300 text-sm">
            <Info className="h-4 w-4" />
            <span>{actionError}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleRefresh}>
              {t('knowledgeBase.refresh')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setActionError(null)}
            >
              {t('common.close')}
            </Button>
          </div>
        </div>
      )}

      {/* Actions and List Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('knowledgeBase.refresh')}
        </Button>
        <ViewToggle
          viewMode={viewMode}
          onToggle={() =>
            setViewMode(prev => (prev === 'full' ? 'compact' : 'full'))
          }
        />
        <Button onClick={() => setShowUploadModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('knowledgeBase.uploadFile')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setSearchResults([]);
            setShowSearchModal(true);
          }}
        >
          <Search className="h-4 w-4 mr-2" />
          Buscar conteudo (IA)
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtrar arquivos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder="Nome, descricao ou tag"
              value={listFilterQuery}
              onChange={event => setListFilterQuery(event.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
            />
            <select
              value={statusFilter}
              onChange={event =>
                setStatusFilter(
                  event.target.value as
                    | 'all'
                    | 'ready'
                    | 'processing'
                    | 'error'
                    | 'pending'
                )
              }
              className="select-control"
            >
              <option value="all">Todos os status</option>
              <option value="ready">Pronto</option>
              <option value="processing">Processando</option>
              <option value="pending">Pendente</option>
              <option value="error">Com erro</option>
            </select>
            <select
              value={fileTypeFilter}
              onChange={event => setFileTypeFilter(event.target.value)}
              className="select-control"
            >
              <option value="all">Todos os tipos</option>
              {availableFileTypes.map(fileType => (
                <option key={fileType} value={fileType}>
                  {fileType.toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={tagFilter}
              onChange={event => setTagFilter(event.target.value)}
              className="select-control"
            >
              <option value="all">Todas as tags</option>
              {availableTags.map(tag => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Knowledge Sources List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white">
            {t('knowledgeBase.sources')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner />
            </div>
          ) : filteredKnowledgeSources.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('knowledgeBase.noFilesFound')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('knowledgeBase.uploadFilesToStart')}
              </p>
              <Button onClick={() => setShowUploadModal(true)}>
                <Upload className="h-4 w-4 mr-2" />
                {t('knowledgeBase.makeUpload')}
              </Button>
            </div>
          ) : viewMode === 'compact' ? (
            <div className="space-y-2">
              {filteredKnowledgeSources.map(source => (
                <div
                  key={source.id}
                  className="flex items-center justify-between p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  title={`${source.name} - ${
                    source.description || ''
                  } | ${formatFileSize(source.file_size)} | ${
                    source.embeddings_count
                  } ${t('knowledgeBase.embeddings')}`}
                >
                  <div
                    className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => handleViewChunks(source)}
                  >
                    {getFileIcon(source.file_type)}
                    <div className="flex flex-col min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {source.name}
                      </h3>
                      <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        ID: {source.id}
                      </span>
                    </div>
                    {getStatusBadge(source)}
                  </div>
                  <div className="ml-2 hidden lg:block">
                    {renderPipeline(source)}
                  </div>

                  <div
                    className="flex items-center space-x-1 flex-shrink-0"
                    onClick={e => e.stopPropagation()}
                  >
                    {source.processing_status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleProcess(source.id)}
                        className="h-7 w-7 p-0"
                        title={t('knowledgeBase.processing')}
                      >
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                    )}

                    {source.processing_status === 'completed' &&
                      source.embeddings_status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGenerateEmbeddings(source.id)}
                          className="h-7 w-7 p-0"
                          title={t('knowledgeBase.embeddingsGenerationStarted')}
                        >
                          <Brain className="h-3 w-3" />
                        </Button>
                      )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        window.open(
                          knowledgeSourceService.getDownloadUrl(source.id),
                          '_blank'
                        )
                      }
                      className="h-7 w-7 p-0"
                      title={t('common.download') || 'Download'}
                    >
                      <Download className="h-3 w-3" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteClick(source.id)}
                      className="h-7 w-7 p-0"
                      title={t('common.delete')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredKnowledgeSources.map(source => (
                <div
                  key={source.id}
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div
                    className="flex items-center space-x-4 flex-1 cursor-pointer"
                    onClick={() => handleViewChunks(source)}
                  >
                    {getFileIcon(source.file_type)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          {source.name}
                        </h3>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          ID: {source.id}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {source.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatFileSize(source.file_size)}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {source.embeddings_count}{' '}
                          {t('knowledgeBase.embeddings')}
                        </span>
                        {source.tags && source.tags.length > 0 && (
                          <div className="flex space-x-1 flex-wrap">
                            {(() => {
                              const validTags = normalizeTags(source.tags);

                              return validTags.slice(0, 3).map((tag, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  size="sm"
                                >
                                  {tag}
                                </Badge>
                              ));
                            })()}
                            {(() => {
                              const validTags = normalizeTags(source.tags);
                              return validTags.length > 3 ? (
                                <Badge variant="secondary" size="sm">
                                  +{validTags.length - 3}
                                </Badge>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </div>
                      <div className="mt-2">{renderPipeline(source)}</div>
                      {(source.processing_error || source.embeddings_error) && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                          {source.processing_error || source.embeddings_error}
                        </p>
                      )}
                    </div>
                  </div>

                  <div
                    className="flex items-center space-x-2"
                    onClick={e => e.stopPropagation()}
                  >
                    {getStatusBadge(source)}

                    {source.processing_status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleProcess(source.id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Processar
                      </Button>
                    )}

                    {source.processing_status === 'completed' &&
                      source.embeddings_status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGenerateEmbeddings(source.id)}
                        >
                          <Brain className="h-4 w-4 mr-2" />
                          Embeddings
                        </Button>
                      )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        window.open(
                          knowledgeSourceService.getDownloadUrl(source.id),
                          '_blank'
                        )
                      }
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteClick(source.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {totalSources > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {filteredKnowledgeSources.length} / {totalSources}{' '}
            {t('knowledgeBase.sources').toLowerCase()}
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title={t('knowledgeBase.uploadFile')}
        size="md"
      >
        <div className="space-y-4">
          <div
            onDrop={handleDropFiles}
            onDragOver={e => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={e => {
              e.preventDefault();
              setIsDragOver(false);
            }}
            className={`rounded-lg border-2 border-dashed p-4 transition-colors ${
              isDragOver
                ? 'border-orange-400 bg-orange-50/60 dark:bg-orange-900/20'
                : 'border-gray-300 dark:border-gray-700'
            }`}
          >
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('knowledgeBase.fileMaxSize')} (ate {MAX_FILES_PER_UPLOAD} arquivos)
            </label>
            <input
              type="file"
              multiple
              accept=".pdf,.csv,.txt,.docx,.xls,.xlsx"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 dark:file:bg-primary-900/20 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-800/30"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Tipos suportados: PDF, CSV, TXT, DOCX, XLS, XLSX
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Arraste e solte arquivos aqui ou selecione no campo acima.
            </p>
          </div>

          {selectedFiles.length > 0 && (
            <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {selectedFiles.length} arquivo(s) selecionado(s)
              </p>
              <div className="mt-2 max-h-36 overflow-auto space-y-1">
                {selectedFiles.map(file => (
                  <p
                    key={`${file.name}-${file.size}`}
                    className="text-xs text-gray-600 dark:text-gray-400"
                  >
                    {file.name} - {formatFileSize(file.size)}
                  </p>
                ))}
              </div>
            </div>
          )}

          {selectedFiles.length <= 1 ? (
            <Input
              label={t('knowledgeBase.name')}
              value={uploadData.name}
              onChange={e =>
                setUploadData(prev => ({ ...prev, name: e.target.value }))
              }
              placeholder={t('knowledgeBase.fileName')}
              required
            />
          ) : null}

          <Input
            label={t('knowledgeBase.description')}
            value={uploadData.description}
            onChange={e =>
              setUploadData(prev => ({ ...prev, description: e.target.value }))
            }
            placeholder={t('knowledgeBase.contentDescription')}
          />

          <Input
            label={t('knowledgeBase.tags')}
            value={uploadData.tags}
            onChange={e =>
              setUploadData(prev => ({ ...prev, tags: e.target.value }))
            }
            placeholder={t('knowledgeBase.tagsPlaceholder')}
          />

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              loading={isUploading}
              disabled={
                selectedFiles.length === 0 ||
                (selectedFiles.length === 1 && !uploadData.name.trim())
              }
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('knowledgeBase.upload')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Search Results Modal */}
      <Modal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        title="Buscar conteudo (IA)"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-64">
              <Input
                placeholder={t('knowledgeBase.searchKnowledge')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                leftIcon={<Search className="h-5 w-5" />}
              />
            </div>
            <Button
              onClick={handleSearch}
              loading={isSearching}
              disabled={!searchQuery.trim()}
            >
              <Search className="h-4 w-4 mr-2" />
              {t('common.search')}
            </Button>
          </div>

          {!searchQuery.trim() ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              {t('knowledgeBase.searchKnowledge')}
            </p>
          ) : !searchResults || searchResults.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
              {t('knowledgeBase.noResultsFound')} "{searchQuery}"
            </p>
          ) : (
            searchResults.map((result, index) => (
              <div
                key={index}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {result.knowledge_source_name}
                  </h4>
                  <Badge variant="primary">
                    {(result.similarity_score * 100).toFixed(1)}%{' '}
                    {t('knowledgeBase.similar')}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {result.content}
                </p>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* RAG Results Modal */}
      <Modal
        isOpen={showRAGModal}
        onClose={() => setShowRAGModal(false)}
        title={t('knowledgeBase.ragQuery')}
        size="xl"
      >
        <div className="space-y-4">
          <Input
            label={t('knowledgeBase.query')}
            value={ragQuery}
            onChange={e => setRagQuery(e.target.value)}
            placeholder={t('knowledgeBase.enterQuestion')}
          />

          <Button
            onClick={handleRAGQuery}
            loading={isRAGing}
            disabled={!ragQuery.trim()}
            className="w-full"
          >
            <Brain className="h-4 w-4 mr-2" />
            {t('knowledgeBase.executeRAGQuery')}
          </Button>

          {ragResults && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {t('knowledgeBase.foundContext')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {ragResults.context}
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {t('knowledgeBase.sourcesUsed')} (
                  {ragResults.total_sources_used}):
                </h4>
                <div className="space-y-2">
                  {ragResults.sources.map((source: any, index: number) => (
                    <div
                      key={index}
                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {source.knowledge_source_name}
                        </span>
                        <Badge variant="primary">
                          {(source.similarity_score * 100).toFixed(1)}%
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {source.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Chunks Modal */}
      <Modal
        isOpen={showChunksModal}
        onClose={() => {
          setShowChunksModal(false);
          setSelectedKnowledgeSource(null);
          setChunks([]);
        }}
        title={
          selectedKnowledgeSource
            ? `${t('knowledgeBase.chunks')} - ${selectedKnowledgeSource.name}`
            : t('knowledgeBase.chunks')
        }
        size="xl"
      >
        <div className="space-y-4">
          {isLoadingChunks ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner />
            </div>
          ) : chunks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>{t('knowledgeBase.noChunksFound')}</p>
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('knowledgeBase.totalChunks')}: {chunks.length}
              </div>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {chunks.map((chunk, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t('knowledgeBase.chunk')} #{index + 1}
                      </span>
                      {chunk.similarity_score !== undefined && (
                        <Badge variant="primary">
                          {(chunk.similarity_score * 100).toFixed(1)}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                      {chunk.content}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setFileToDelete(null);
        }}
        onConfirm={handleDelete}
        message={t('knowledgeBase.confirmDeleteFile')}
        variant="danger"
      />

      {/* Insufficient Tokens Modal */}
      <InsufficientTokensModal
        isOpen={isModalOpen}
        onClose={closeModal}
        tokensBalance={tokensBalance}
      />

      {/* Buy Tokens Modal */}
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={closeBuyTokensModal}
      />
    </div>
  );
};

export default KnowledgeBase;
